import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, combineLatest } from 'rxjs';
import { isContentCategoryVisible } from '../../../content/content-tree.helper';
import { ContentService } from '../../../content/content.service';
import { PmCoverageService } from '../../../leave-and-coverage/services/pm-coverage.service';
import { MessagesModalComponent } from '../../../messages-modal/messages-modal/messages-modal.component';
import { NotificationsModalComponent } from '../../../notifications-modal/notifications-modal/notifications-modal.component';
import { ElementVisibilityDecisionMakerDirective } from '../../../permissions/element-visibility-decision-maker.directive';
import { DropDownComponent, DropDownInterface } from '../../../shared/components/drop-down/drop-down.component';
import { LanguageSwitchComponent } from '../../../shared/components/language-switch/language-switch.component';
import { ListModeSwitchComponent } from '../../../shared/components/list-mode-switch/list-mode-switch.component';
import { ListModeSwitchService } from '../../../shared/components/list-mode-switch/list-mode-switch.service';
import { SaleMode } from '../../../shared/components/list-mode-switch/sale-mode.types';
import { NotificationComponent } from '../../../shared/components/notification/notification.component';
import { ActivatePricelistDirective } from '../../../shared/directives/activate-pricelist/activate-pricelist.directive';
import { ImageSizeEnum } from '../../../shared/pipes/resize-image/resize-image.pipe';
import { SharedModule } from '../../../shared/shared.module';
import { LeaveAndCoverageTabsEnum } from '../../enums/leave-and-coverage-tabs.enum';
import { RolePermissionEnum } from '../../enums/role-permission.enum';
import { GeneralRoutePaths, InquiriesRouteType, OrderListRouteType, RouteTypeEnums } from '../../enums/route-types.enum';
import { UserPermissionsEnum } from '../../enums/user-permissions.enum';
import { ImpersonateService } from '../../impersonate/impersonate.service';
import { UnimpersonateComponent } from '../../impersonate/unimpersonate/unimpersonate.component';
import { UserInterface } from '../../models/user.model';
import { CoverageSessionService } from '../../services/coverage/coverage-session.service';
import { PermissionsService } from '../../services/permissions/permissions.service';
import { PriceListStoreHandlerService } from '../../services/price-list/price-list-store-handler.service';
import { UserService } from '../../services/user/user.service';
import { LogoComponent } from '../logo/logo.component';
import { NavbarElementInterface, NavbarElements, OriginalNavbarElements } from '../navbar-elements';
import { NavbarWrapperComponent } from '../navbar-wrapper/navbar-wrapper.component';
import { TreeService } from '../tree.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    NavbarWrapperComponent,
    LogoComponent,
    RouterLink,
    ElementVisibilityDecisionMakerDirective,
    DropDownComponent,
    ActivatePricelistDirective,
    NotificationComponent,
    LanguageSwitchComponent,
    UnimpersonateComponent,
    ListModeSwitchComponent,
  ],
})
export class NavbarComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private modalService = inject(NgbModal);
  private priceListService = inject(PriceListStoreHandlerService);
  private treeService = inject(TreeService);
  private routerService = inject(Router);
  private listModeSwitchService = inject(ListModeSwitchService);
  private permissionsService = inject(PermissionsService);
  private contentService = inject(ContentService);
  private coverageSessionService = inject(CoverageSessionService);
  private pmCoverageService = inject(PmCoverageService);
  private impersonateService = inject(ImpersonateService);

  private subscription: Subscription = new Subscription();
  public user: UserInterface;

  elements: NavbarElementInterface[] = [];
  selectedElement: NavbarElementInterface = null;
  permissionActions = RolePermissionEnum;
  saleMode: SaleMode;
  generalRoutePaths = GeneralRoutePaths;
  leaveAndCoverageTabs = LeaveAndCoverageTabsEnum;
  hasPermissionToViewCompanyProfile = false;
  isDealer = false;
  isDealerAdmin = false;
  isPM = false;
  isCoveringActive = false;
  imageSizes = ImageSizeEnum;
  isNarbutasLT = false;

  priceList: DropDownInterface = {
    selected: null,
    values: [],
    onSelect: (value) => {
      this.priceListService.switch(value, true);
    },
  };

  ngOnInit() {
    // Update user information on navbar init for notifications
    this.userService.fromStorage().subscribe((user) => {
      this.user = user;
      this.isDealer = this.userService.isDealer();
      this.isPM = this.userService.isPM();
      this.isNarbutasLT = this.userService.isNarbutasLT();

      // Init services when app fully loads
      setTimeout(() => {
        this.priceListService.init();
      });

      this.subscription.add(
        combineLatest([
          this.contentService.getContentTree(GeneralRoutePaths.INFO),
          this.permissionsService.getUserPermissionsAsObservable(),
        ]).subscribe(([infoContent]) => {
          this.elements = NavbarElements.map((element) => {
            if (element.id === RouteTypeEnums.ORDERS) {
              element.children.map((child) => {
                const shouldBeHidden =
                  OriginalNavbarElements.find((el) => el.id === RouteTypeEnums.ORDERS).children.find((c) => c.id === child.id)
                    .hideFromMenu ?? false;

                if ([OrderListRouteType.OFFERS].includes(child.id)) {
                  child.hideFromMenu = shouldBeHidden || !this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_AND_CREATE_OFFERS);
                  return child;
                }

                if (
                  [
                    OrderListRouteType.ACTIVE_ORDERS,
                    OrderListRouteType.DONE,
                    OrderListRouteType.PLANNED_LOADINGS,
                    OrderListRouteType.FINISHED_LOADINGS,
                  ].includes(child.id)
                ) {
                  child.hideFromMenu = shouldBeHidden || !this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_ORDERS);
                  return child;
                }
              });
            }

            if (element.id === RouteTypeEnums.INQUIRIES) {
              element.children.map((child) => {
                const shouldBeHidden =
                  OriginalNavbarElements.find((el) => el.id === RouteTypeEnums.INQUIRIES).children.find((c) => c.id === child.id)
                    .hideFromMenu ?? false;

                if (child.id === InquiriesRouteType.PROJECT_RESERVATIONS) {
                  child.hideFromMenu =
                    shouldBeHidden || !this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_AND_CREATE_PROJECT_RESERVATIONS);
                }

                if (child.id === InquiriesRouteType.CUSTOM_MADE_PRICE_REQUESTS) {
                  child.hideFromMenu = shouldBeHidden || !this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_CMPR);
                }

                if (child.id === InquiriesRouteType.CLAIMS) {
                  child.hideFromMenu = shouldBeHidden || !this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_AND_CREATE_CLAIMS);
                }
              });
            }

            return element;
          }).filter((element) => {
            if (element.hideFromMenu) {
              return false;
            }

            if (element.id === RouteTypeEnums.CATALOGUE && !this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_CATALOGUE)) {
              return false;
            }

            if (
              element.id === RouteTypeEnums.ORDERS &&
              !this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_AND_CREATE_OFFERS) &&
              !this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_ORDERS)
            ) {
              return false;
            }

            if (
              element.id === RouteTypeEnums.ESTIMATES &&
              !this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_ORDERS_LEAD_TIME)
            ) {
              return false;
            }

            if (element.id === RouteTypeEnums.INFO && !infoContent.data.some(isContentCategoryVisible)) {
              return false;
            }

            if (element.id === RouteTypeEnums.INQUIRIES && !this.permissionsService.canViewInquiries()) {
              return false;
            }

            return !element.allowedRoles || (element.allowedRoles && element.allowedRoles.indexOf(user.role.name) > -1);
          });

          this.hasPermissionToViewCompanyProfile = this.permissionsService.canViewCompanyProfile();
          this.isDealerAdmin = this.permissionsService.hasUserAdminPermission();
        }),
      );
    });

    // Subscribe for user data changes
    this.subscription.add(this.userService.userObservable().subscribe((updatedUser) => (this.user = updatedUser)));

    this.subscription.add(
      this.coverageSessionService.isCoveringActiveObservable().subscribe((isCoveringActive) => {
        this.isCoveringActive = isCoveringActive;
      }),
    );

    this.subscription.add(
      this.userService.poll().subscribe(
        (updatedUser) => (this.user = updatedUser),
        (error) => {
          this.subscription.unsubscribe();
        },
      ),
    );
    this.subscription.add(
      this.treeService.getState().subscribe(({ first }) => {
        this.selectedElement = first;
      }),
    );

    this.subscription.add(
      this.listModeSwitchService.saleModeAsObservable().subscribe((saleMode: SaleMode) => {
        this.saleMode = +saleMode;
      }),
    );

    this.priceListService.getCurrentAsObservable().subscribe((priceList) => {
      this.priceList.selected = priceList;
    });

    this.priceListService.getAll().subscribe((priceLists) => {
      this.priceList.values = priceLists;
    });
  }

  getRouterLinkPath(navElement: NavbarElementInterface): string {
    if (!navElement.children?.length || navElement.path === GeneralRoutePaths.INFO) {
      return navElement.path;
    }

    // Find first applicable sub-nav element for current user
    const [secondaryNavElement] = navElement.children
      .filter(({ allowedRoles }) => !allowedRoles || allowedRoles.indexOf(this.user?.role?.name) > -1)
      .sort((a, b) => {
        if (!(a.positions && b.positions)) {
          return 0;
        }
        return (
          a.positions.find(({ role }) => role === this.user?.role?.name).position -
          b.positions.find(({ role }) => role === this.user?.role?.name).position
        );
      });

    if (!secondaryNavElement) {
      return navElement.path;
    }

    return `${navElement.path}/${secondaryNavElement.path}`;
  }

  isActive(navElement: NavbarElementInterface): boolean {
    const treeState = this.treeService.getStateValue();
    return treeState.first?.id === navElement.id;
  }

  onClick(navElement: NavbarElementInterface) {
    if ([RouteTypeEnums.CATALOGUE, RouteTypeEnums.ORDERS, RouteTypeEnums.INQUIRIES].indexOf(navElement.id) > -1) {
      this.treeService.open(navElement, null, true);
      return;
    }
    this.treeService.modifyState({ first: navElement, second: null, third: null });
  }

  onMessagesModalOpen() {
    this.modalService.open(MessagesModalComponent, {
      windowClass: 'messages-modal',
    });
  }

  onNotificationsModalOpen() {
    this.modalService.open(NotificationsModalComponent, {
      windowClass: 'messages-modal',
      scrollable: true,
    });
  }

  get isUserOnLeave(): boolean {
    return this.userService.isOnLeaveNow();
  }

  /**
   * Clears all the routing data set by user
   */
  ngOnDestroy() {
    this.user = null;
    this.subscription.unsubscribe();
    this.treeService.closeAll();
  }

  onLogout() {
    // preserve picked language and drop everything else
    const userLanguage = sessionStorage.getItem('language');
    sessionStorage.clear();
    sessionStorage.setItem('language', userLanguage);

    this.routerService.navigate([`/${GeneralRoutePaths.LOGOUT}`]);
  }

  onStopCovering() {
    this.subscription.add(
      this.pmCoverageService.stopCovering().subscribe({
        next: () => {
          this.routerService.navigate([`/${GeneralRoutePaths.LEAVE_AND_COVERAGE}`]);
        },
      }),
    );
  }

  get userBadgeClasses(): string {
    if (this.saleMode) {
      return 'bg-warning color-white sale-mode';
    }

    return this.isCoveringActive ? 'bg-primary color-gray-200' : 'shown-bg-gray-900 shown-color-gray-400';
  }

  get showLeaveAndCoverageNavElement(): boolean {
    return this.isPM && !this.isCoveringActive && !this.impersonateService.impersonated() && !this.isUserOnLeave;
  }
}
